import { Controller } from "@hotwired/stimulus"

// Writes the System / Light / Dark / named-theme choice and applies it on <html>
// in the same turn.
//
// Saved twice, for two readers (FRO-155). The PATCH puts it on the user, so it follows them to
// every browser and the server paints it on the first HTML byte. The cookie is the mirror for
// what has no current_user — the login page — and for first paint before that save has landed.
// The cookie is unsigned because JS cannot produce a signature Rails would accept, and it
// authorizes nothing. There is no boot script: CSP is script-src 'self', so an inline one
// cannot run.
//
// Hovering or focusing a gallery card previews that theme on the whole page without saving;
// leaving the gallery or pressing Esc puts the committed theme back, and a click commits.
//
// A year, because a preference that quietly resets is worse than none.
const COOKIE_MAX_AGE_SECONDS = 31536000

export default class extends Controller {
  static values = { cookie: String, url: String, colors: Object }

  disconnect() {
    // A Turbo visit swaps <body> but keeps <html>, so a preview left stamped there would follow
    // the reader off the page as if it were their theme.
    this.endPreview()
  }

  change() {
    const preference = this.#selected

    this.previewing = false
    this.#persist(preference)
    this.#save(preference)
    this.#apply(preference)
    this.#syncThemeColor(preference)
  }

  pick(event) {
    const id = event.currentTarget.dataset.appearanceThemeParam
    const input = this.element.querySelector(`#appearance_${id}`)

    if (!input) return

    input.checked = true
    this.change()
  }

  preview(event) {
    // Esc means "stop showing me things" until the pointer leaves the gallery and comes back.
    if (this.suppressed && event.type === "mouseenter") return

    const id = event.currentTarget.dataset.appearanceThemeParam
    if (!id) return

    this.previewing = true
    this.#apply(id)
  }

  endPreview(event) {
    if (event?.type === "mouseleave") this.suppressed = false
    if (!this.previewing) return

    this.previewing = false
    this.#apply(this.#selected)
  }

  cancelPreview() {
    if (!this.previewing) return

    this.suppressed = true
    this.endPreview()
  }

  get #selected() {
    const checked = this.element.querySelector("input[name='appearance']:checked")

    return checked?.value || "system"
  }

  #apply(preference) {
    const root = document.documentElement

    if (preference === "system") {
      root.removeAttribute("data-theme")
    } else {
      root.setAttribute("data-theme", preference)
    }
  }

  #persist(preference) {
    const secure = location.protocol === "https:" ? "; Secure" : ""

    document.cookie =
      `${this.cookieValue}=${preference}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  }

  async #save(preference) {
    if (!this.urlValue) return

    try {
      await fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({ appearance: preference })
      })
    } catch {
      // Offline or a blip: the theme still applies here and the cookie keeps it on this browser;
      // it just won't follow the person to another one until the next choice saves.
    }
  }

  #syncThemeColor(preference) {
    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.remove())

    const head = document.head

    if (preference === "system") {
      head.append(this.#themeColorMeta(this.#colorFor("light"), "(prefers-color-scheme: light)"))
      head.append(this.#themeColorMeta(this.#colorFor("dark"), "(prefers-color-scheme: dark)"))
      return
    }

    head.append(this.#themeColorMeta(this.#colorFor(preference)))
  }

  #colorFor(theme) {
    return this.colorsValue[theme] || this.colorsValue.light
  }

  #themeColorMeta(content, media) {
    const meta = document.createElement("meta")

    meta.setAttribute("name", "theme-color")
    meta.setAttribute("content", content)
    if (media) meta.setAttribute("media", media)

    return meta
  }
}
